"use client";

import React, { useState, useEffect, useRef } from "react";
import StatsCard from "../../../../../components/stats-card";
import DeleteButton from "../../../../../components/deleteButton";
import Button from "../../../../../components/Button";
import { Search, ChevronDown } from "lucide-react";
import styles from "../css/MedicationCard.module.css";
import { searchMedications, getMedicationBySku } from "../../../../../api/dispensary/script/medicationCardApi.js";
import Status from "../../../../../components/Status";
import { useAuth } from "../../../../../context/AuthContext.jsx";

const directionsOptions = [{ value: "", label: "Select Directions" },{ value: "Take 1 tablet daily", label: "Take 1 tablet daily" },{ value: "Take 2 tablets twice daily", label: "Take 2 tablets twice daily" },{ value: "Take 1 capsule three times daily", label: "Take 1 capsule three times daily" },{ value: "Apply twice daily", label: "Apply twice daily" },{ value: "As needed", label: "As needed" },{ value: "Other", label: "Other" },];
export default function MedicationCard({
  med,
  onChange,
  onDelete,
  onSave,
  index,
  inventoryDepartmentId,
  showSaveButton = true,
}) {
  const { user } = useAuth();
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState(med.drugName || "");
  const searchTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSearchTerm(med.drugName || "");
  }, [med.drugName]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSearch = async (term) => {
    if (term.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }
    setIsSearching(true);
    try {
      const branchcode = user?.branchcode || undefined;
      const results = await searchMedications({ search: term, branchcode, departmentid: inventoryDepartmentId });
      setSearchResults(results);
      setShowDropdown(true);
    } catch (error) {
      console.error('Error searching medications:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };
  const handleMedicationSelect = async (medication) => {
    setSearchTerm(medication.name);
    setShowDropdown(false);
    setSearchResults([]);
    try {
      const full = await getMedicationBySku(medication.sku);
      const cpRaw = full?.costPrice;
      let cpFormatted = "";
      if (cpRaw !== null && cpRaw !== undefined && cpRaw !== "") {
        const num = Number(cpRaw);
        cpFormatted = Number.isFinite(num) ? `R${num.toFixed(2)}` : `R${cpRaw}`;
      }
      const qRaw = full?.quantity;
      let qtyText = "";
      let qtyNum;
      if (qRaw !== null && qRaw !== undefined && qRaw !== "") {
        const qNum = Number(qRaw);
        qtyNum = Number.isFinite(qNum) ? qNum : undefined;
        const qDisplay = Number.isFinite(qNum) ? (Number.isInteger(qNum) ? `${qNum}` : qNum.toFixed(2)) : `${qRaw}`;
        qtyText = `${qDisplay} units in stock`;
      }
      onChange(med.id, "drugName", full?.name || medication.name || "");
      if (full?.sku) onChange(med.id, "sku", full.sku);
      onChange(med.id, "nappiCode", full?.nappiCode || "");
      onChange(med.id, "packSize", full?.packSize || "");
      onChange(med.id, "strength", full?.strength || "");
      onChange(med.id, "costPrice", cpFormatted);
      if (qtyText) {
        onChange(med.id, "stockText", qtyText);
      } else {
        onChange(med.id, "stockText", "");
      }
      if (qtyNum !== undefined) {
        onChange(med.id, "stockQty", qtyNum);
      } else {
        onChange(med.id, "stockQty", undefined);
      }
      if (Array.isArray(full?.icd10) && full.icd10.length > 0) {
        onChange(med.id, "icd10Options", full.icd10);
        const code = full.icd10[0];
        onChange(med.id, "icd10", code);
      } else {
        onChange(med.id, "icd10Options", []);
        onChange(med.id, "icd10", "");
      }
    } catch (e) {
      const cpRaw = medication?.costPrice;
      const num = Number(cpRaw);
      const cpFormatted = cpRaw ? (Number.isFinite(num) ? `R${num.toFixed(2)}` : `R${cpRaw}`) : "";
      const qRaw = medication?.quantity;
      let qtyNum;
      if (qRaw !== null && qRaw !== undefined && qRaw !== "") {
        const qNum = Number(qRaw);
        qtyNum = Number.isFinite(qNum) ? qNum : undefined;
        const qDisplay = Number.isFinite(qNum) ? (Number.isInteger(qNum) ? `${qNum}` : qNum.toFixed(2)) : `${qRaw}`;
        onChange(med.id, "stockText", `${qDisplay} units in stock`);
        if (qtyNum !== undefined) onChange(med.id, "stockQty", qtyNum); else onChange(med.id, "stockQty", undefined);
      } else {
        onChange(med.id, "stockText", "");
        onChange(med.id, "stockQty", undefined);
      }
      onChange(med.id, "drugName", medication.name || "");
      if (medication?.sku) onChange(med.id, "sku", medication.sku);
      onChange(med.id, "nappiCode", medication.nappiCode || "");
      onChange(med.id, "packSize", medication.packSize || "");
      onChange(med.id, "strength", medication.strength || "");
      onChange(med.id, "costPrice", cpFormatted);
      if (Array.isArray(medication?.icd10) && medication.icd10.length > 0) {
        onChange(med.id, "icd10Options", medication.icd10);
        const code = medication.icd10[0];
        onChange(med.id, "icd10", code);
      } else {
        onChange(med.id, "icd10Options", []);
        onChange(med.id, "icd10", "");
      }
    }
  };
  const handleInputChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      handleSearch(term);
    }, 150);
  };
  return (
    <StatsCard key={med.id} variant="form" interactive={false}>
      <div>
        <div className={styles.cardHeader}>
          <div className={styles.headerLeft}>
            <span className={styles.index}>#{(index ?? 0) + 1}</span>
            {Number.isFinite(Number(med?.stockQty)) && (
              <Status
                status={`${med.stockQty} units in stock`}
                tone={med.stockQty < 50 ? 'red' : med.stockQty < 100 ? 'amber' : 'green'}
              />
            )}
          </div>
          <DeleteButton onClick={() => onDelete(med.id)} />
        </div>
        <div className={styles.mcContent}>
          <div className={styles.mcRow}>
            <div className={`${styles.inputGroup} ${styles.span2}`}>
              <label htmlFor={`drugName-${med.id}`} className={styles.label}>Medication</label>
              <div className={styles.medField} ref={dropdownRef}>
                <Search className={styles.medIconLeft} size={18} />
                <input
                  id={`drugName-${med.id}`}
                  type="text"
                  value={searchTerm}
                  onChange={handleInputChange}
                  onFocus={() => searchTerm.length >= 2 && setShowDropdown(true)}
                  className={`${styles.input} ${styles.medInput}`}
                  placeholder="Select medication"
                  autoComplete="off"
                />
                <ChevronDown className={styles.medCaretRight} size={18} />
                {showDropdown && (
                  <div className={styles.autocompleteDropdown}>
                    {isSearching ? (
                      <div className={styles.dropdownItem}>Searching...</div>
                    ) : searchResults.length > 0 ? (
                      searchResults.map((medication) => (
                        <div
                          key={medication.sku}
                          className={styles.dropdownItem}
                          onClick={() => handleMedicationSelect(medication)}
                        >
                          <div className={styles.medicationName}>{medication.name}</div>
                          <div className={styles.medicationDetails}>
                            {medication.strength && `${medication.strength} `}
                            {medication.packSize && `• ${medication.packSize} `}
                            {medication.quantity && `• Stock: ${medication.quantity}`}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className={styles.dropdownItem}>No medications found</div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className={styles.inputGroup}>
              <label htmlFor={`nappiCode-${med.id}`} className={styles.label}>Nappi Code</label>
              <input
                id={`nappiCode-${med.id}`}
                type="text"
                value={med.nappiCode && String(med.nappiCode).trim() !== "" ? med.nappiCode : "N/A"}
                readOnly
                className={styles.input}
              />
            </div>
          </div>
          <div className={styles.mcRowThree}>
            <div className={`${styles.inputGroup} ${styles.mcSmall}`}>
              <label htmlFor={`packSize-${med.id}`} className={styles.label}>Pack Size</label>
              <input
                id={`packSize-${med.id}`}
                type="text"
                value={med.packSize && String(med.packSize).trim() !== "" ? med.packSize : "N/A"}
                readOnly
                className={styles.input}
              />
            </div>
            <div className={styles.inputGroup}>
              <label htmlFor={`strength-${med.id}`} className={styles.label}>Strength</label>
              <input
                id={`strength-${med.id}`}
                type="text"
                value={med.strength && String(med.strength).trim() !== "" ? med.strength : "N/A"}
                readOnly
                className={styles.input}
              />
            </div>
            <div className={styles.inputGroup}>
              <label htmlFor={`icd10-${med.id}`} className={styles.label}>ICD-10</label>
              <select
                id={`icd10-${med.id}`}
                value={med.icd10 || ""}
                onChange={(e) => onChange(med.id, "icd10", e.target.value)}
                className={styles.select}
              >
                {(Array.isArray(med.icd10Options) ? med.icd10Options : []).length === 0 ? (
                  <option value="" disabled>
                    N/A
                  </option>
                ) : (
                  (Array.isArray(med.icd10Options) ? med.icd10Options : []).map((code) => (
                    <option key={code} value={code}>{code}</option>
                  ))
                )}
              </select>
            </div>
          </div>
          <div className={styles.mcRowThree}>
            <div className={styles.inputGroup}>
              <label htmlFor={`costPrice-${med.id}`} className={styles.label}>Cost Price</label>
              <input
                id={`costPrice-${med.id}`}
                type="text"
                value={med.costPrice && String(med.costPrice).trim() !== "" ? med.costPrice : "N/A"}
                readOnly
                className={styles.input}
                placeholder="R0.00"
              />
            </div>
            <div className={styles.inputGroup}>
              <label htmlFor={`quantity-${med.id}`} className={styles.label}>Quantity</label>
              <input
                id={`quantity-${med.id}`}
                type="number"
                min={0}
                value={med.quantity === 0 ? 0 : (med.quantity ?? "")}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "") { onChange(med.id, "quantity", ""); return; }
                  const clean = v.replace(/[^0-9]/g, "");
                  if (clean !== v) { e.target.value = clean; }
                  const n = Number(clean);
                  if (!Number.isFinite(n)) { return; }
                  onChange(med.id, "quantity", Math.max(0, Math.floor(n)));
                }}
                onKeyDown={(e) => {
                  if (["-", "+", ".", ",", "e", "E"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                inputMode="numeric"
                pattern="[0-9]*"
                className={styles.input}
              />
            </div>
            <div className={styles.inputGroup}>
              <label className={styles.label}>&nbsp;</label>
              <div className={styles.mb6}>
                {(() => {
                  const q = Number(med?.quantity);
                  const s = Number(med?.stockQty);
                  if (Number.isFinite(q) && Number.isFinite(s) && q > s) {
                    const diff = q - s;
                    return (
                      <span
                        className={`${styles.hintBadge} ${styles.hintBadgeClickable} ${med.toFollowStruck ? styles.hintBadgeStruck : ""}`}
                        onClick={() => onChange(med.id, "toFollowStruck", !med.toFollowStruck)}
                      >
                        {diff} to follow
                      </span>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>
          </div>
          <div className={styles.mcRow}>
            <div className={`${styles.inputGroup} ${((Number.isFinite(Number(med?.quantity)) && Number.isFinite(Number(med?.stockQty)) && Number(med.quantity) > Number(med.stockQty)) ? styles.span2 : styles.spanAll)}`}>
              <label htmlFor={`instructions-${med.id}`} className={styles.label}>Directions</label>
              <select
                id={`instructions-${med.id}`}
                value={med.instructions}
                onChange={(e) => onChange(med.id, "instructions", e.target.value)}
                className={styles.select}
              >
                {directionsOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              {med.instructions === "Other" && (
                <input
                  type="text"
                  value={med.instructionsOther || ""}
                  onChange={(e) => onChange(med.id, "instructionsOther", e.target.value)}
                  className={`${styles.input} ${styles.mt8}`}
                  placeholder="Enter directions"
                />
              )}
            </div>
            <div className={styles.inputGroup}>
              {(() => {
                const q = Number(med?.quantity);
                const s = Number(med?.stockQty);
                if (Number.isFinite(q) && Number.isFinite(s) && q > s && !med.toFollowStruck) {
                  return (
                    <>
                      <label htmlFor={`deliveryOption-${med.id}`} className={styles.label}>Option</label>
                      <select
                        id={`deliveryOption-${med.id}`}
                        value={med.deliveryOption}
                        onChange={(e) => onChange(med.id, "deliveryOption", e.target.value)}
                        className={styles.select}
                      >
                        <option value="" disabled hidden>Select Delivery Option</option>
                        <option value="Collect">Collect</option>
                        <option value="Deliver">Deliver</option>
                      </select>
                    </>
                  );
                }
                return null;
              })()}
            </div>
          </div>
        </div>
        {showSaveButton && (
          <Button variant="primary" onClick={() => onSave(med.id)} className={styles.saveButton}>
            Save
          </Button>
        )}
      </div>
    </StatsCard>
  );
}
